import { persistenceHealth } from "../analytics/persistence";
import { useCallback, useEffect, useRef, useState } from "react";

import { getSceneVersion } from "@excalidraw/excalidraw";

import { isInitializedImageElement } from "@excalidraw/element";

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";

import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { isCollaborationLink } from "../data";
import { DRAWOLA_FEATURES } from "../drawola-features";

import { drawolaT } from "./i18n";
import { runMigrations } from "./migrate";
import {
  createDoc,
  getDoc,
  listDocs,
  saveDoc,
  setActiveDocId,
} from "./multiFileStore";
import { canWrite, requestPersist } from "./quota";
import { refreshDocThumbnail, THUMBNAIL_MIN_INTERVAL_MS } from "./thumbnails";
import { estimateDocBytes } from "./usage";

import type { CanWriteResult } from "./types";
import type { ImportedScene } from "../studio/importDoc";

type QuotaError = Exclude<CanWriteResult, { ok: true }>;

type State = {
  activeDocId: string | null;
  booted: boolean;
  refreshToken: number;
  quotaError: QuotaError | null;
};

type Scene = {
  elements: readonly OrderedExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
};

type SceneIdentity = {
  version: number;
  elementsLength: number;
  appState: AppState;
  files: BinaryFiles;
};

/**
 * Reports whether this page load is entering a live collaboration room.
 * Mirrors the gate in `App.tsx`: with `SHOW_LIVE_COLLAB` off a `#room=` link
 * is ignored and its hash stripped, so it must not change anything here.
 */
const isOpeningCollaborationLink = (): boolean =>
  DRAWOLA_FEATURES.SHOW_LIVE_COLLAB &&
  isCollaborationLink(window.location.href);

/**
 * Owns the multi-file store lifecycle for App.tsx:
 *   - boot: run migrations, pick active doc, prime the scene
 *   - save: debounced write-through for every scene change
 *   - switch: flush current save, load target doc into Excalidraw
 */
export function useMultiFileStore(
  excalidrawAPI: ExcalidrawImperativeAPI | null,
  enabled: boolean,
  /**
   * Asked, live, whether the canvas currently belongs to no draw yet.
   *
   * A joined collaboration room resets the canvas and fills it with somebody
   * else's scene, so between that reset and the session getting a draw of its
   * own there is nothing this store may legitimately write: whatever it wrote
   * would land in the draw the user happened to have open. Passed as a
   * function rather than a boolean so the answer is read at the moment of the
   * write, not at the moment of the last render. See
   * `collabSessionDocPendingAtom` in `collab/Collab.tsx`.
   */
  shouldHoldSaves?: () => boolean,
) {
  const [state, setState] = useState<State>({
    activeDocId: null,
    booted: false,
    refreshToken: 0,
    quotaError: null,
  });
  const activeDocIdRef = useRef<string | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const pendingRef = useRef<Scene | null>(null);

  // Closed while the active doc is being taken away from the editor. The
  // canvas still holds the OUTGOING scene during that window, so everything it
  // reports belongs to a doc that is already gone and must be dropped, never
  // written to whichever doc takes its place.
  const savesHeldRef = useRef(false);

  // Kept current on every render so the check below always asks the caller's
  // latest predicate, never one captured in a stale closure.
  const shouldHoldSavesRef = useRef(shouldHoldSaves);
  shouldHoldSavesRef.current = shouldHoldSaves;

  /** Whether anything written right now would land in the wrong document. */
  const savesHeld = useCallback(
    (): boolean =>
      savesHeldRef.current || shouldHoldSavesRef.current?.() === true,
    [],
  );

  // Latest scene handed to `scheduleSave`, kept AFTER the save flushes so a
  // later `flushSave()` can still produce a preview even when there is nothing
  // left to write. Cleared whenever the active doc changes, so we never stamp
  // one draw's preview onto another.
  const sceneRef = useRef<Scene | null>(null);
  const thumbRef = useRef({ dirty: false, lastAt: 0, running: false });
  const lastAcceptedSceneRef = useRef<SceneIdentity | null>(null);

  /** Forget the outgoing doc's scene, save identity, and thumbnail state. */
  const resetDocScopedState = useCallback(() => {
    sceneRef.current = null;
    thumbRef.current.dirty = false;
    lastAcceptedSceneRef.current = null;
  }, []);

  // boot: migrate + load active doc
  useEffect(() => {
    if (!enabled || !excalidrawAPI) {
      return;
    }
    let cancelled = false;
    let resolveEditorReady!: () => void;
    const editorReady = new Promise<void>((resolve) => {
      resolveEditorReady = resolve;
    });
    let unsubscribeEditorChange: (() => void) | undefined;
    const unsubscribeEditorReady = () => {
      unsubscribeEditorChange?.();
      unsubscribeEditorChange = undefined;
    };
    // Upstream puts a scene into the editor at boot too: `initializeScene()` in
    // App.tsx reads the ONE global localStorage scene and hands it over as
    // `initialData`. Nothing ordered that against our own load, so whoever
    // landed last won the canvas, and the debounced save then wrote it into the
    // active doc. That let a foreign draw overwrite the open one.
    //
    // Upstream emits `onChange` only once `isLoading` turned false, and that is
    // exactly when it applied `initialData`, so the first event is the proof
    // that our scene can safely go on top.
    //
    // Subscribed here and not inside the async body below: this effect runs
    // before the editor scene load effect in the same commit, so the event
    // cannot be missed. After an await it could already be gone.
    unsubscribeEditorChange = excalidrawAPI.onChange(() => {
      unsubscribeEditorReady();
      resolveEditorReady();
    });
    (async () => {
      // fire-and-forget persist request on boot
      requestPersist().catch(() => undefined);

      // Resolve localized default doc name so the first/auto-created
      // document uses the user's language instead of a hardcoded English
      // "Untitled". drawolaT reads from upstream's current langCode.
      const { activeDocId } = await runMigrations(
        drawolaT("files.untitledPrefix"),
      );
      if (cancelled) {
        return;
      }
      await editorReady;
      if (cancelled) {
        return;
      }
      activeDocIdRef.current = activeDocId;
      resetDocScopedState();
      if (activeDocId) {
        const doc = await getDoc(activeDocId);
        if (doc && !cancelled) {
          // mirror doc's files into Excalidraw
          if (Object.keys(doc.files).length) {
            const arr = Object.values(doc.files);
            excalidrawAPI.addFiles(arr);
          }
          // An empty active doc must replace any legacy scene from upstream.
          //
          // A collaboration link is the one case where the canvas does not
          // belong to a local document: the room hands its scene over through
          // the relay moments after the editor is ready, and priming from the
          // active doc on top of that left a joining peer staring at an empty
          // canvas. Read from `window.location` rather than taken as an
          // argument, so a later change of collaboration state cannot re-run
          // this one-shot boot effect. The files above still go in, because
          // they only ever add.
          if (!isOpeningCollaborationLink()) {
            excalidrawAPI.updateScene({
              elements: doc.elements as any,
            });
          }
        }
      }
      setState((s) => ({
        ...s,
        activeDocId,
        booted: true,
      }));
    })()
      .catch((e) => {
        // don't crash the app — multi-file store is best-effort in MVP
        // eslint-disable-next-line no-console
        console.error("[drawOla] multi-file boot failed", e);
        setState((s) => ({ ...s, booted: true }));
      })
      .finally(unsubscribeEditorReady);
    return () => {
      cancelled = true;
      unsubscribeEditorReady();
      resolveEditorReady();
    };
  }, [enabled, excalidrawAPI, resetDocScopedState]);

  /**
   * Regenerate the active doc's Studio preview.
   *
   * Throttled to one render per `THUMBNAIL_MIN_INTERVAL_MS` of active editing;
   * `force` (only ever set by `flushSave`) skips the throttle so the list is
   * fresh at exactly the moments the user is about to look at it — switching
   * draws, or going back to the Studio.
   *
   * Never throws: `refreshDocThumbnail` already swallows everything, and the
   * one non-forced caller does not await it at all.
   */
  const refreshThumbnail = useCallback(
    async (force: boolean) => {
      if (!enabled) {
        return;
      }
      const id = activeDocIdRef.current;
      const scene = sceneRef.current;
      const thumb = thumbRef.current;
      if (!id || !scene || !thumb.dirty || thumb.running) {
        return;
      }
      if (!force && Date.now() - thumb.lastAt < THUMBNAIL_MIN_INTERVAL_MS) {
        return;
      }
      thumb.running = true;
      try {
        const changed = await refreshDocThumbnail(
          id,
          scene.elements,
          scene.appState,
          scene.files,
        );
        thumb.dirty = false;
        // Only the forced path needs a re-list: the debounced path has just
        // bumped `refreshToken` from the save itself, and the Studio re-lists
        // on mount anyway.
        if (changed && force) {
          setState((s) => ({ ...s, refreshToken: s.refreshToken + 1 }));
        }
      } finally {
        // back off after failures too, so a scene that cannot render does not
        // get retried on every single debounce tick
        thumb.lastAt = Date.now();
        thumb.running = false;
      }
    },
    [enabled],
  );

  // debounced save-through — use-case: onChange fires on every tiny edit
  const savePending = useCallback(async (): Promise<boolean> => {
    const p = pendingRef.current;
    pendingRef.current = null;
    saveTimerRef.current = null;
    if (!p) {
      // nothing new to write — but the preview may still be stale
      return true;
    }
    if (savesHeld()) {
      // The hold can be taken after this scene was queued and before the
      // debounce fires, so it has to be checked here too. The scene is dropped
      // rather than kept: it belongs to the document handover we are in the
      // middle of, and by the time saving is allowed again the active document
      // is a different one.
      return false;
    }
    const id = activeDocIdRef.current;
    if (!id) {
      return false;
    }
    // Upstream's files map only grows within a session: `addMissingFiles`
    // rebuilds it from `this.files` plus the new entries and never removes
    // anything. So a deleted image keeps its fileId in the map, and without
    // this filter the refCount in the store never reaches zero and the bytes
    // stay in IndexedDB forever. Drop files whose id is no longer referenced
    // by a non-deleted image element in the same scene.
    const referencedFileIds = new Set<string>();
    for (const element of p.elements) {
      if (!element.isDeleted && isInitializedImageElement(element)) {
        referencedFileIds.add(element.fileId);
      }
    }
    let files = p.files;
    if (Object.keys(files).some((fid) => !referencedFileIds.has(fid))) {
      files = {};
      for (const fid of referencedFileIds) {
        if (p.files[fid]) {
          files[fid] = p.files[fid];
        }
      }
    }
    const next = estimateDocBytes(p.elements, p.appState, files);
    try {
      const check = await canWrite(next, id);
      if (!check.ok) {
        persistenceHealth.record(
          false,
          new DOMException("Storage quota", "QuotaExceededError"),
        );
        setState((s) => ({ ...s, quotaError: check }));
        return false;
      }
      await saveDoc(id, {
        elements: p.elements,
        appState: p.appState,
        files,
      });
      persistenceHealth.record(true);
      setState((s) => ({ ...s, refreshToken: s.refreshToken + 1 }));
      return true;
    } catch (e: any) {
      persistenceHealth.record(false, e);
      if (e?.name === "QuotaExceededError") {
        setState((s) => ({
          ...s,
          quotaError: {
            ok: false,
            reason: "quota",
            meta: {},
          },
        }));
      } else {
        // eslint-disable-next-line no-console
        console.error("[drawOla] save failed", e);
      }
      return false;
    }
  }, [savesHeld]);

  const flushPending = useCallback(
    async (forceThumbnail = false) => {
      const saved = await savePending();
      if (!saved) {
        // blocked by quota or the write failed — do not spend a render on a
        // preview of a scene that is not on disk
        return;
      }
      if (forceThumbnail) {
        await refreshThumbnail(true);
      } else {
        // fire-and-forget: the debounce tick sits on the editing path
        void refreshThumbnail(false);
      }
    },
    [refreshThumbnail, savePending],
  );

  const scheduleSave = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      if (!enabled) {
        return;
      }
      if (savesHeld()) {
        // These elements belong to a doc that is being removed, or to a
        // collaboration room whose own draw is not open yet. See
        // `onActiveDeleted` and `shouldHoldSaves`.
        return;
      }
      if (!activeDocIdRef.current) {
        return;
      }
      // Upstream calls `onChange` on every render, not only on real edits, and
      // a finished save bumps `refreshToken`, which causes such a render. That
      // was a loop writing the same doc once per second while nobody touched
      // the canvas. Compare by scene version and NOT by the `elements` array
      // reference: Excalidraw mutates elements in place and bumps
      // `element.version`, so the array can stay the same object across a real
      // edit, and a reference check would silently drop it.
      const version = getSceneVersion(elements);
      const lastAcceptedScene = lastAcceptedSceneRef.current;
      if (
        lastAcceptedScene?.version === version &&
        lastAcceptedScene.elementsLength === elements.length &&
        lastAcceptedScene.appState === appState &&
        lastAcceptedScene.files === files
      ) {
        return;
      }
      lastAcceptedSceneRef.current = {
        version,
        elementsLength: elements.length,
        appState,
        files,
      };
      const scene: Scene = { elements, appState, files };
      pendingRef.current = scene;
      sceneRef.current = scene;
      thumbRef.current.dirty = true;
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        flushPending();
      }, 500);
    },
    [enabled, flushPending, savesHeld],
  );

  const flushSave = useCallback(() => {
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return flushPending(true);
  }, [flushPending]);

  /**
   * Throw away whatever the debounce is still holding, without writing it.
   *
   * Only for a doc that is gone: its queued scene is garbage, and `savePending`
   * reads the active doc id at flush time, so leaving it queued would send it
   * to the doc that comes next.
   */
  const discardPendingSave = useCallback(() => {
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingRef.current = null;
  }, []);

  const switchTo = useCallback(
    async (docId: string) => {
      if (!excalidrawAPI) {
        return;
      }
      await flushSave();
      const doc = await getDoc(docId);
      if (!doc) {
        return;
      }
      await setActiveDocId(docId);
      activeDocIdRef.current = docId;
      resetDocScopedState();
      // swap scene
      excalidrawAPI.updateScene({
        elements: (doc.elements ?? []) as any,
      });
      if (Object.keys(doc.files).length) {
        excalidrawAPI.addFiles(Object.values(doc.files));
      }
      setState((s) => ({
        ...s,
        activeDocId: docId,
        refreshToken: s.refreshToken + 1,
      }));
    },
    [excalidrawAPI, flushSave, resetDocScopedState],
  );

  /**
   * Make `docId` the active draw WITHOUT touching the canvas.
   *
   * `switchTo` is for the user picking a different draw, so it replaces the
   * scene with that draw's stored one. A live collaboration session is the
   * opposite case: the room is the source of the scene and hands it over on
   * its own, so replacing the canvas here would throw the room's drawing away.
   * The draw only has to become the place that scene gets saved to.
   *
   * No `flushSave` either: while this is being called the save gate is shut
   * (see `shouldHoldSaves`), so there is nothing pending that belongs anywhere.
   */
  const adoptDoc = useCallback(
    async (docId: string) => {
      await setActiveDocId(docId);
      activeDocIdRef.current = docId;
      resetDocScopedState();
      setState((s) => ({
        ...s,
        activeDocId: docId,
        refreshToken: s.refreshToken + 1,
      }));
    },
    [resetDocScopedState],
  );

  /**
   * Put a scene that arrived from outside the store (a `#json=` shared link,
   * a `#url=` external scene) into a NEW doc and open it.
   *
   * Opens with `switchTo` and never with `onCreated`: `onCreated` resets the
   * canvas to an empty scene, which would throw away the content we just
   * seeded. Same constraint as `cloud/useCloudStorage.ts` `openFile`.
   *
   * @returns the new doc id, or `null` when the editor is not ready yet.
   */
  const importSceneAsNewDoc = useCallback(
    async (name: string, scene: ImportedScene): Promise<string | null> => {
      if (!excalidrawAPI) {
        return null;
      }
      const meta = await createDoc(name, scene);
      await switchTo(meta.id);
      return meta.id;
    },
    [excalidrawAPI, switchTo],
  );

  const onCreated = useCallback(
    async (docId: string) => {
      if (!excalidrawAPI) {
        return;
      }
      await flushSave();
      activeDocIdRef.current = docId;
      resetDocScopedState();
      // reset scene to empty
      excalidrawAPI.updateScene({ elements: [] });
      setState((s) => ({
        ...s,
        activeDocId: docId,
        refreshToken: s.refreshToken + 1,
      }));
    },
    [excalidrawAPI, flushSave, resetDocScopedState],
  );

  /**
   * The active doc was deleted: open a stored fallback or a new untitled doc.
   *
   * Invariant: nothing may be written to the next doc until the canvas really
   * holds that doc's scene. The editor still shows the deleted doc while this
   * runs, so its scene is dropped (`discardPendingSave`) and the door is kept
   * shut (`savesHeldRef`) instead of following the active doc id, which used to
   * hand the outgoing scene straight to the next doc and destroy its content.
   */
  const onActiveDeleted = useCallback(async () => {
    if (!excalidrawAPI) {
      return;
    }
    discardPendingSave();
    resetDocScopedState();
    savesHeldRef.current = true;
    try {
      const docs = await listDocs();
      const fallback =
        docs[0] ?? (await createDoc(drawolaT("files.untitledPrefix")));
      await switchTo(fallback.id);
    } finally {
      savesHeldRef.current = false;
    }
  }, [discardPendingSave, excalidrawAPI, resetDocScopedState, switchTo]);

  const dismissQuotaError = useCallback(() => {
    setState((s) => ({ ...s, quotaError: null }));
  }, []);

  /**
   * Bump `refreshToken` after a doc-list mutation this hook did not perform
   * itself (currently: the editor breadcrumb's inline rename). Every consumer
   * that re-reads on `refreshToken` — the Studio grid, the Files sidebar —
   * picks the change up.
   */
  const notifyDocsChanged = useCallback(() => {
    setState((s) => ({ ...s, refreshToken: s.refreshToken + 1 }));
  }, []);

  return {
    booted: state.booted,
    activeDocId: state.activeDocId,
    refreshToken: state.refreshToken,
    quotaError: state.quotaError,
    scheduleSave,
    flushSave,
    discardPendingSave,
    switchTo,
    adoptDoc,
    importSceneAsNewDoc,
    onCreated,
    onActiveDeleted,
    dismissQuotaError,
    notifyDocsChanged,
  };
}
